// Envoi d'un événement à Meta : le pixel du navigateur ET l'API Conversions,
// sous le MÊME identifiant d'événement.
//
// À utiliser partout où l'on appelait auparavant graph.facebook.com depuis le
// navigateur avec le jeton d'accès en clair dans l'URL. Le client ne transmet
// plus que le nom de l'événement, ses données commerciales et cet identifiant :
// l'identité et l'adresse IP sont dérivées de la session et des en-têtes côté
// serveur (app/api/meta/event/route.js).
//
// POURQUOI LES DEUX D'UN SEUL GESTE. Chaque endroit du site appelait
// `window.fbq("track", ...)` puis cette fonction, et le serveur tirait de son
// côté un identifiant au hasard : Meta ne pouvait donc PAS reconnaître les deux
// envois comme une seule action, et comptait la conversion deux fois. Le
// dédoublonnage exige le même `event_id` des deux côtés (fbq le reçoit sous le
// nom `eventID`), d'où le couple fabriqué ici, une fois.
//
// L'appel n'attend rien et n'échoue jamais bruyamment : une mesure marketing
// ne doit pas casser un tunnel d'achat, ni retarder une redirection.
//
// Rien ne part sans l'accord « publicité » du visiteur (lib/cookieConsent.js),
// ni le pixel ni l'API Conversions : les deux servent la mesure publicitaire.

import { hasConsent } from "@/lib/cookieConsent";

/** Un identifiant par action, partagé par le pixel et l'API Conversions. */
function nouvelIdentifiantEvenement() {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch (error) {
        // Contexte non sécurisé ou navigateur ancien : repli ci-dessous.
    }
    return `bm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * @param {string} eventName nom d'événement Meta (ViewContent, Purchase...)
 * @param {object} [customData] données commerciales (value, currency, content_ids...)
 * @returns {string|null} l'identifiant de l'événement, ou null si rien n'est parti
 */
export function sendMetaEvent(eventName, customData) {
    if (typeof window === "undefined" || !eventName) return null;
    if (!hasConsent("marketing")) return null;

    const eventId = nouvelIdentifiantEvenement();

    // Le pixel d'abord : c'est lui qui porte les cookies _fbp et _fbc, donc la
    // meilleure correspondance. Le serveur repasse derrière pour les visiteurs
    // dont le pixel est bloqué.
    try {
        if (window.fbq) {
            window.fbq("track", eventName, customData || {}, {
                eventID: eventId,
            });
        }
    } catch (error) {
        // Un pixel absent ou en erreur ne doit jamais interrompre la page.
    }

    try {
        const body = JSON.stringify({
            eventName,
            eventId,
            eventSourceUrl: window.location.href,
            customData: customData || undefined,
        });

        // keepalive : l'envoi survit à la navigation qui suit souvent ces
        // événements (redirection après paiement ou inscription).
        fetch("/api/meta/event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        }).catch(() => {});
    } catch (error) {
        // Sérialisation impossible : on abandonne silencieusement.
    }

    return eventId;
}
